<template>
  <div>
    <box>
      <h1 class="mb-4">Arbitrage stats</h1>
      <div class="d-flex flex-wrap">
        <date-picker-compare
          class="mr-3"
          align="left"
          :show-compare="false"
          v-model="selection.dateRanges"
        >
          <template slot-scope="props">
            <span class="cursor-pointer btn-link-alt">
              {{ props.rangeValues.main.start | formatDate }} -
              {{ props.rangeValues.main.end | formatDate }}
            </span>
            <svg-icon
              icon="chevron-down"
              class="icon-xs text-lighten ml-2 align-middle"
            />
          </template>
        </date-picker-compare>
        <configurator
          class="mr-3"
          name="segments"
          @submit="submitSegment"
          :filters="segments"
          @get-segments="getSegments"
          ref="segmentsConfigurator"
          :custom-empty-text="$t('noSegments')"
        >
          <div class="settings" slot="settings">
            <button
              class="item border-0 w-100 py-2 pr-2 d-flex pl-3"
              @click="openForm(false)"
            >
              <span>New segment</span>
            </button>
            <button
              v-if="segments.length"
              class="item border-0 w-100 py-2 pr-2 d-flex pl-3"
              @click="openForm(true)"
            >
              <span>Edit segment</span>
            </button>
          </div>
        </configurator>
        <configurator
          class="mr-3"
          name="dimensions"
          @submit="submitDimension"
          :filters="dimensionsModel"
        />
        <list-segments
          class="d-flex-item"
          :class="{ 'mr-3': selection.activeSegments.length }"
          v-model="selection.activeSegments"
          type="dimension"
        >
          <div
            v-if="dimensionsModel.length > 0"
            class="float-configuration size-default"
            slot="popover"
            slot-scope="{ item, index, isOpen, popoverHidden }"
          >
            <div v-if="isOpen">
              <dimension-configurator
                :configuration="getConfigurationByIndex(index)"
                @submit="submitDimension($event, index, popoverHidden)"
              />
            </div>
          </div>
        </list-segments>
        <configurator
          class="mr-3"
          :name="'group by'"
          uniq-key="key"
          @submit="submitGroup"
          :filters="dimensions"
        />
        <list-segments
          class="d-flex-item"
          :class="{ 'mr-3': selection.groupBy.length }"
          v-model="selection.groupBy"
          type="group-by"
          :disable-sort="false"
          one-required
        />
      </div>
    </box>
    <segment-form
      v-if="isSegmentForm"
      :is-edit="isEditSegment"
      :dimensions="dimensionsModel"
      namespace="arbitrage-stats"
      @close-segment-form="isSegmentForm = false"
    />
    <template v-if="hasStats">
      <box v-if="stats.table.data.length" class="p-3 rounded-0 border-top">
        <chart-stats-rtb
          :groups="selection.groupBy"
          :data="stats.table.data"
          :metrics="metrics"
        />
      </box>
      <div class="d-flex">
        <columns-toggle
          namespace="admin-stats"
          class="ml-auto"
          :fields="[...metrics]"
          @columns-toggled="updateFields"
        >
          <div slot="top">
            <b-form-checkbox v-model="hideZeroColumns">
              Hide zero columns
            </b-form-checkbox>
            <hr />
          </div>
        </columns-toggle>
      </div>
      <box class="p-0 border-top-0 wrapper">
        <table-stats
          :data="stats.table.data"
          :fields="stats.table.fields"
          :group-by="selection.groupBy"
          :busy="isLoadingStats"
          :hide-zero-columns="hideZeroColumns"
        />
      </box>
    </template>
    <box v-else-if="!isLoadingStats" class="text-center text-light">
      <h2>{{ $t('noData') }}</h2>
      <p class="mb-0">{{ $t('noDataWithConditions') }}</p>
    </box>
  </div>
</template>

<script>
import DatePickerCompare from '@/components/Forms/DatePickerCompare.vue';
import DimensionMenu from './../components/DimensionMenu.vue';
import ListSegments from './../components/ListSegments.vue';
import SegmentForm from './../components/SegmentForm.vue';
import { saverMixin } from '@/utils/mixins.js';
import { toUTCDateISOString } from '@/utils/date.js';
import TableStats from '@/components/TableStats/index.vue';
import ChartStatsRtb from './../components/ChartStatsRtb.vue';
import Configurator from './../components/Configurator.vue';
import { mapActions, mapGetters } from 'vuex';
import cloneDeep from 'clone';
import groupBy from 'lodash.groupby';
import ColumnsToggle from '@/components/TableStats/ColumnsToggle.vue';
import Tree from '@/components/TreeView/Tree.vue';
import notFilterableDimensions from './../nonFilterableDimensions';
import DimensionConfigurator from './../components/DimensionConfigurator.vue';
import Saver from '@/utils/searchParamsSaver.js';
import merge from 'merge';

export default {
  mixins: [saverMixin],

  components: {
    DatePickerCompare,
    DimensionMenu,
    ListSegments,
    TableStats,
    ChartStatsRtb,
    Configurator,
    ColumnsToggle,
    Tree,
    DimensionConfigurator,
    SegmentForm
  },

  data() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let end = this.$date.addHours(new Date(today), 24);
    end = this.$date.addMinutes(new Date(end), -1);
    let start = this.$date.subDays(new Date(today), 6);
    const defaultRange = {
      start: start,
      end: end
    };

    return {
      isEditSegment: false,
      isSegmentForm: false,
      saver: null,
      isLoadingStats: false,
      hasStats: true,
      hideZeroColumns: Boolean(Saver.readFromLocalStorage('hideZeroColumns')),
      segments: [],
      selection: {
        dateRanges: {
          main: defaultRange
        },
        activeSegments: [],
        groupBy: [
          {
            key: 'date',
            name: 'Date',
            valueType: 'date'
          }
        ]
      },
      stats: {
        table: {
          sortBy: 'date',
          sortDesc: false,
          fields: [],
          enabledFields: [],
          data: []
        }
      }
    };
  },

  computed: {
    form() {
      const prepareRange = range => {
        const prepared = {};
        prepared.start = toUTCDateISOString(range.start, true);
        prepared.end = toUTCDateISOString(range.end, true);
        return prepared;
      };

      const range = prepareRange(this.selection.dateRanges.main);
      return {
        from: range.start,
        to: range.end,
        groups: this.selection.groupBy.map(el => el.key),
        filters: this.selection.activeSegments.reduce((filters, el) => {
          const prepare = prepareEl => {
            let values = null;
            if (Array.isArray(prepareEl.value))
              values = prepareEl.value.map(el => el.id);
            else values = [prepareEl.value];

            return {
              key: prepareEl.key,
              values: values,
              condition: prepareEl.condition
            };
          };

          if (el.type === 'range') {
            el.value.min &&
              filters.push(
                prepare({
                  ...el,
                  value: el.value.min,
                  condition: '>='
                })
              );
            el.value.max &&
              filters.push(
                prepare({
                  ...el,
                  value: el.value.max,
                  condition: '<='
                })
              );
          } else filters.push(prepare(el));

          return filters;
        }, [])
      };
    },
    ...mapGetters({
      dimensions: 'advert/dimensions',
      metrics: 'advert/metrics'
    }),
    availableFields() {
      const availableDimensions = this.dimensions
        .filter(el => this.availableDimensions.includes(el.key))
        .filter(el =>
          this.selection.groupBy.find(searchEl => searchEl.key !== el.key)
        );

      const availableMetrics = this.metrics.filter(el =>
        this.availableMetrics.includes(el.key)
      );
      return (
        [...availableDimensions, ...availableMetrics].filter(el => {
          return this.stats.table.enabledFields.find(
            enabledField => enabledField.key === el.key
          );
        }) || []
      );
    },
    dimensionsModel() {
      const dimensionsModel = this.dimensions
        .map(el => {
          const form = cloneDeep(this.form);
          const { child } = el;
          form.groups = [el.key];
          if (child) form.groups = form.groups.concat([...child]);

          form.filters = form.filters.filter(filter => filter.key !== el.key);
          let url = `${this.$api.adnet.options.baseUrl}/stats/values`;
          let query = `?q=${JSON.stringify(form)}`;

          return {
            name: el.name,
            key: el.key,
            configuration: {
              type: 'checkbox',
              excludable: true,
              options: url + query
            }
          };
        })
        .filter(el => !notFilterableDimensions.includes(el.key));

      const metricsModel = this.metrics.map(el => {
        return {
          name: el.name,
          key: el.key,
          configuration: {
            type: 'range',
            excludable: true
          }
        };
      });

      if (dimensionsModel.length === 0 || metricsModel.length === 0) return [];
      return [...dimensionsModel, ...metricsModel];
    },
    availableDimensions() {
      if (this.dimensions.length === 0 || this.stats.table.data.length === 0)
        return [];
      const dimensionsKeys = this.dimensions.map(el => el.key);
      return Object.keys(this.stats.table.data[0]).filter(f =>
        dimensionsKeys.includes(f)
      );
    },
    availableMetrics() {
      if (this.metrics.length === 0 || this.stats.table.data.length === 0)
        return [];
      const metricsKeys = this.metrics.map(el => el.key);
      return Object.keys(this.stats.table.data[0]).filter(f =>
        metricsKeys.includes(f)
      );
    }
  },

  methods: {
    getSegments() {
      this.segments =
        JSON.parse(
          localStorage.getItem(
            `${this.$store.state.app.role}-arbitrage-stats-segments`
          )
        ) || [];
    },
    getSegmentByName(name) {
      const segments = this.segments.length
        ? this.segments
        : JSON.parse(
            localStorage.getItem(
              `${this.$store.state.app.role}-arbitrage-stats-segments`
            )
          );

      return segments.find(item => {
        return item.name === name;
      });
    },
    openForm(isEdit) {
      this.isSegmentForm = true;
      this.isEditSegment = !!isEdit;
      this.$refs.segmentsConfigurator.hide();
    },
    submitSegment(segment, index, popoverHidden) {
      const segmentItem = this.getSegmentByName(segment);
      let segments = [];

      Object.keys(segmentItem.dimensions).forEach(key => {
        const item = segmentItem.dimensions[key];
        const emitValueTemplate = {
          exclude: item.isExclude,
          configurationName: item.name,
          key: item.id
        };
        let dimension = {};

        if (item.configuration.type === 'range') {
          dimension = {
            ...emitValueTemplate,
            value: item.range,
            type: item.configuration.type
          };
        } else {
          dimension = {
            ...emitValueTemplate,
            value: item.values,
            type: item.configuration.type,
            condition: item.isExclude ? '!=' : '='
          };
        }

        segments.push(dimension);
      });
      this.selection.activeSegments = segments;
      popoverHidden && popoverHidden(index);
    },
    submitDimension(segment, index, popoverHidden) {
      if (index !== undefined) {
        this.$set(this.selection.activeSegments, index, segment);
      } else this.selection.activeSegments.push(segment);
      popoverHidden && popoverHidden(index);
    },
    submitGroup(group) {
      if (this.selection.groupBy.length === 3) {
        this.$notify('alert', {
          text: 'Only three groups maximum available.',
          variant: 'warning',
          time: 5000
        });
        return false;
      }
      const searchKey = group.name;
      const addAccess = this.selection.groupBy.findIndex(el => {
        const key = el.name;
        return key === searchKey;
      });
      if (addAccess !== -1) {
        this.selection.groupBy.splice(addAccess, 1);
      }
      this.selection.groupBy.push(group);
    },
    ...mapActions({
      fetchMetrics: 'advert/fetchMetrics',
      fetchDimensions: 'advert/fetchDimensions',
      fetchStatisticsAction: 'advert/fetchStatistics'
    }),
    fetchStatistics() {
      this.isLoadingStats = true;
      this.hasStats = true;
      this.fetchStatisticsAction({ q: this.form })
        .then(res => {
          this.stats.table.data = res;
          this.hasStats = res.length > 0;
          this.proccedTableFields();
          this.isLoadingStats = false;
        })
        .catch(e => {
          this.$notify('alert', {
            text: e.message,
            variant: 'danger',
            time: 5000
          });
        });
    },
    proccedTableFields() {
      if (this.availableFields.length === 0) return;
      const groupedColumns = groupBy(cloneDeep(this.availableFields), el => {
        if (el.key.includes('seller')) return 'seller';
        if (el.key.includes('buyer')) return 'buyer';
        else return 'common';
      });

      this.stats.table.fields = [];
      groupedColumns.common &&
        this.stats.table.fields.push({
          name: 'Common',
          children: [...groupedColumns.common]
        });

      groupedColumns.buyer &&
        this.stats.table.fields.push({
          name: 'Buyer',
          children: groupedColumns.buyer.map(el => {
            el.name = el.name.replace('Buyer ', '');
            return el;
          })
        });

      groupedColumns.seller &&
        this.stats.table.fields.push({
          name: 'Seller',
          children: groupedColumns.seller.map(el => {
            el.name = el.name.replace('Seller ', '');
            return el;
          })
        });
    },
    updateFields(fields) {
      this.stats.table.enabledFields = fields;
      this.proccedTableFields();
    },
    getConfigurationByIndex(index) {
      const dimension = this.selection.activeSegments[index];
      const dimensionModel = this.dimensionsModel.find(
        el => el.key === dimension.key
      );

      return {
        ...dimension,
        key: dimension.key,
        name: dimension.name || dimension.configurationName,
        options: dimensionModel.configuration.options
      };
    }
  },

  async created() {
    await Promise.all([this.fetchMetrics(), this.fetchDimensions()]);
    this.saver = new Saver(this.$router, {
      queryParameter: 'q',
      storageName: 'arbitrage-stats'
    });
    const urlSelection = this.saver.getFromUrl();
    if (urlSelection) this.selection = merge(this.selection, urlSelection);

    this.fetchStatistics();
  },

  watch: {
    availableFields() {
      this.proccedTableFields();
    },
    form() {
      this.fetchStatistics();
    },
    selection: {
      deep: true,
      handler() {
        this.saver.saveToUrl(this.selection);
      }
    },
    hideZeroColumns(val) {
      Saver.saveToLocalStorage('hideZeroColumns', val);
    }
  }
};
</script>

<style lang="scss">
.btn-group-toggle-wide {
  width: 100%;
}
.float-configuration {
  margin: -0.5rem -0.75rem;
  border-radius: $border-radius;
  overflow: hidden;
}
.settings {
  border-bottom: 1px solid $border-color;
  .item {
    cursor: pointer;
    outline: none;

    &:hover {
      background: lighten($lighten, 30%);
    }

    &.active {
      background: lighten($secondary, 25%);
    }
  }
}
</style>
